/**
 * ConfigurationEditModal - Configuration 편집 모달
 * ConfigurationBuilder를 포함하여 커리큘럼/상품의 Configuration을 편집
 * 템플릿에서 불러오기 기능 포함
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Loader2, Settings, BookOpen, Package, FileText, Check, Edit3 } from 'lucide-react'
import type { LessonConfiguration, ConfigurationTemplateResponse } from '@/types/configuration'
import { createDefaultConfiguration } from '@/types/configuration'
import * as configApi from '@/api/configuration'
import { ConfigurationBuilder } from './ConfigurationBuilder'

interface ConfigurationEditModalProps {
  targetType: 'curriculum' | 'product'
  targetId: number
  targetName: string
  configuration: LessonConfiguration | null
  onClose: () => void
  onSave: (config: LessonConfiguration) => Promise<void>
  isSaving?: boolean
}

type TabType = 'edit' | 'template'

export function ConfigurationEditModal({
  targetType,
  targetId: _targetId,
  targetName,
  configuration,
  onClose,
  onSave,
  isSaving = false
}: ConfigurationEditModalProps) {
  // _targetId is reserved for future use (e.g., logging, analytics)
  const [activeTab, setActiveTab] = useState<TabType>(configuration ? 'edit' : 'template')
  const [currentConfig, setCurrentConfig] = useState<LessonConfiguration>(
    configuration || createDefaultConfiguration()
  )
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)

  // 템플릿 목록 조회
  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['configuration', 'templates'],
    queryFn: () => configApi.getTemplates(),
    staleTime: 60 * 1000,
  })

  const templates = templatesData?.templates || []

  const handleSave = async () => {
    await onSave(currentConfig)
  }

  const handleSelectTemplate = (template: ConfigurationTemplateResponse) => {
    setSelectedTemplateId(template.id)
    setCurrentConfig(template.configuration)
  }

  const handleApplyTemplate = () => {
    // 선택된 템플릿이 이미 currentConfig에 설정되어 있으므로 편집 탭으로 이동
    setActiveTab('edit')
  }

  const targetTypeLabel = targetType === 'curriculum' ? '커리큘럼' : '상품'
  const TargetIcon = targetType === 'curriculum' ? BookOpen : Package

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Settings className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">학습진행 설정 편집</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                  <TargetIcon className="w-3 h-3" />
                  {targetTypeLabel}
                </span>
                <span className="text-sm text-slate-500">{targetName}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-6 flex-shrink-0">
          <button
            onClick={() => setActiveTab('template')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'template'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText className="w-4 h-4 inline-block mr-2" />
            템플릿에서 불러오기
          </button>
          <button
            onClick={() => setActiveTab('edit')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'edit'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Edit3 className="w-4 h-4 inline-block mr-2" />
            직접 편집
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'template' ? (
            <TemplateSelector
              templates={templates}
              isLoading={isLoadingTemplates}
              selectedTemplateId={selectedTemplateId}
              onSelect={handleSelectTemplate}
            />
          ) : (
            <ConfigurationBuilder
              initialConfiguration={currentConfig}
              onChange={setCurrentConfig}
              showPreview={false}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <div>
            {activeTab === 'template' && selectedTemplateId && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <Check className="w-4 h-4" />
                템플릿이 선택되었습니다. "적용 후 편집" 또는 "바로 저장"을 클릭하세요.
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={isSaving}
            >
              취소
            </button>
            {activeTab === 'template' && selectedTemplateId && (
              <button
                onClick={handleApplyTemplate}
                className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                적용 후 편집
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || (activeTab === 'template' && !selectedTemplateId)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  저장 중...
                </>
              ) : (
                '저장'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// 템플릿 선택 컴포넌트
interface TemplateSelectorProps {
  templates: ConfigurationTemplateResponse[]
  isLoading: boolean
  selectedTemplateId: number | null
  onSelect: (template: ConfigurationTemplateResponse) => void
}

function TemplateSelector({ templates, isLoading, selectedTemplateId, onSelect }: TemplateSelectorProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-slate-500">템플릿 로딩 중...</span>
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">등록된 템플릿이 없습니다.</p>
        <p className="text-sm text-slate-400 mt-1">"직접 편집" 탭에서 새 설정을 만들어주세요.</p>
      </div>
    )
  }

  // 컴포넌트 표시 이름
  const getComponentName = (type: string, location?: string): string => {
    if (type === 'quiz') {
      const quizLabels: Record<string, string> = {
        pre: 'Pre Quiz',
        post: 'Post Quiz',
        wrapup: 'Wrapup Quiz',
        aiq: 'AIQ Quiz'
      }
      return quizLabels[location || 'post'] || 'Quiz'
    }
    const names: Record<string, string> = {
      video: 'Video',
      assignment: '생각 정리하기',
      reading: '읽기',
    }
    return names[type] || type
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 mb-4">
        아래 템플릿 중 하나를 선택하여 적용하세요. 선택 후 필요에 따라 편집할 수 있습니다.
      </p>
      {templates.map((template) => {
        const isSelected = selectedTemplateId === template.id
        const enabledComponents = template.configuration.sequence.filter(c => c.enabled)

        return (
          <div
            key={template.id}
            onClick={() => onSelect(template)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              isSelected
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-slate-900">{template.name}</h4>
                  {template.isSystemDefault && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      시스템 기본
                    </span>
                  )}
                  {isSelected && (
                    <Check className="w-5 h-5 text-indigo-600" />
                  )}
                </div>
                {template.description && (
                  <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                )}
                {/* 학습 순서 미리보기 */}
                <div className="flex items-center gap-1 mt-2 flex-wrap">
                  {enabledComponents.map((component, index) => (
                    <span key={`${component.type}-${component.location || index}`} className="flex items-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        component.required
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {getComponentName(component.type, component.location)}
                      </span>
                      {index < enabledComponents.length - 1 && (
                        <span className="text-slate-400 mx-1">→</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
